import { useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { GUILDS, REGIONS } from "../data/seed";
import { usePortal } from "../state/PortalContext";
import { Button, Card, Pill } from "../components/ui";
import { Icon } from "../components/Icon";
import { Logo } from "../components/Logo";

export function LandingPage() {
  const { user, signInDemo, signInWithGoogle, signInWithMagicLink, isDemo } =
    usePortal();
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  if (user) {
    return (
      <Navigate
        to={user.role === "student" ? "/app/map" : "/faculty"}
        replace
      />
    );
  }

  const handleEmail = async (event: FormEvent) => {
    event.preventDefault();
    setLoading(true);
    try {
      setMessage(await signInWithMagicLink(email));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Unable to sign in.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    setMessage("");
    try {
      await signInWithGoogle();
    } catch (error) {
      setMessage(
        error instanceof Error ? error.message : "Unable to continue with Google.",
      );
      setGoogleLoading(false);
    }
  };

  return (
    <div className="min-h-screen overflow-hidden">
      <header className="relative z-20 mx-auto flex max-w-7xl items-center justify-between px-5 py-5 sm:px-8">
        <Logo />
        <a
          href="#sign-in"
          className="rounded-xl px-4 py-2 text-sm font-bold text-slate-300 transition hover:bg-slate-800 hover:text-white"
        >
          Sign in
        </a>
      </header>

      <main>
        <section className="relative mx-auto grid max-w-7xl items-center gap-12 px-5 pb-20 pt-12 sm:px-8 lg:grid-cols-[1.02fr_.98fr] lg:pb-28 lg:pt-20">
          <div className="relative z-10">
            <Pill tone="amber" className="mb-6">
              <Icon name="Sparkles" size={13} />
              Mathematics, reimagined as an adventure
            </Pill>
            <h1 className="font-display text-5xl font-bold leading-[0.98] tracking-tight text-white sm:text-6xl lg:text-7xl">
              Welcome to the Wonderful World of{" "}
              <span className="bg-gradient-to-r from-amber-300 via-emerald-300 to-cyan-300 bg-clip-text text-transparent">
                Essential Math for Games
              </span>
            </h1>
            <p className="mt-7 max-w-2xl text-lg leading-8 text-slate-300">
              Explore a living game world, solve interactive systems, master the
              mathematics behind movement and physics, and build confidence one
              quest at a time.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <a href="#sign-in">
                <Button variant="gold" className="w-full px-7 sm:w-auto">
                  <Icon name="Gamepad2" size={20} />
                  Start your journey
                </Button>
              </a>
              <a href="#world">
                <Button variant="secondary" className="w-full px-7 sm:w-auto">
                  <Icon name="Map" size={19} />
                  Explore the world
                </Button>
              </a>
            </div>
            <div className="mt-9 flex flex-wrap gap-x-7 gap-y-3 text-xs font-bold text-slate-400">
              <span className="flex items-center gap-2">
                <Icon name="Check" size={16} className="text-emerald-300" /> 9
                themed regions
              </span>
              <span className="flex items-center gap-2">
                <Icon name="Check" size={16} className="text-emerald-300" />{" "}
                Competency mastery
              </span>
              <span className="flex items-center gap-2">
                <Icon name="Check" size={16} className="text-emerald-300" />{" "}
                Interactive puzzles
              </span>
            </div>
          </div>

          <div id="world" className="relative min-h-[520px]">
            <div className="absolute left-1/2 top-1/2 h-[520px] w-[520px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-indigo-500/10 blur-3xl" />
            <Card className="map-terrain relative h-[500px] overflow-hidden border-indigo-400/20 shadow-glow">
              <div className="absolute left-5 top-5 z-10">
                <p className="font-retro text-[8px] tracking-widest text-cyan-200">
                  THE NINE REALMS
                </p>
                <p className="mt-2 text-xs text-slate-500">
                  Your course is the world map.
                </p>
              </div>
              <svg
                viewBox="0 0 100 100"
                preserveAspectRatio="none"
                className="absolute inset-0 h-full w-full opacity-55"
                aria-hidden="true"
              >
                <path
                  d="M 10 72 Q 15 58 22 53 Q 28 67 34 73 Q 39 50 45 38 Q 50 44 56 61 Q 61 73 66 78 Q 71 58 76 48 Q 82 39 84 25 M 66 78 Q 63 45 68 17"
                  fill="none"
                  stroke="#818cf8"
                  strokeWidth="0.8"
                  className="path-dash"
                />
              </svg>
              {REGIONS.map((region, index) => (
                <div
                  key={region.id}
                  className="absolute z-10 flex -translate-x-1/2 -translate-y-1/2 flex-col items-center"
                  style={{ left: `${region.x}%`, top: `${region.y}%` }}
                >
                  <div
                    className="grid h-12 w-12 animate-float place-items-center rounded-2xl border-2 bg-slate-950 shadow-lg"
                    style={{
                      borderColor: region.color,
                      animationDelay: `${index * -0.35}s`,
                    }}
                  >
                    <Icon
                      name={region.icon}
                      size={20}
                      style={{ color: region.color }}
                    />
                  </div>
                  <span className="mt-1.5 hidden rounded-md bg-slate-950/85 px-2 py-1 text-[9px] font-bold text-slate-200 sm:block">
                    {region.name}
                  </span>
                </div>
              ))}
              <div className="absolute bottom-5 left-5 right-5 z-20 rounded-2xl border border-slate-700/60 bg-slate-950/85 p-4 backdrop-blur">
                <div className="flex items-center gap-3">
                  <span className="rounded-xl bg-amber-400/10 p-2 text-amber-300">
                    <Icon name="Trophy" size={20} />
                  </span>
                  <div>
                    <p className="font-display font-bold text-white">
                      Learn by doing
                    </p>
                    <p className="text-xs text-slate-400">
                      Every region turns game-development mathematics into a
                      visual challenge.
                    </p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </section>

        <section className="border-y border-slate-800/80 bg-slate-950/30 py-20">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <div className="mx-auto max-w-3xl text-center">
              <p className="font-retro text-[9px] tracking-[0.2em] text-amber-300">
                CHOOSE YOUR PLAY STYLE
              </p>
              <h2 className="mt-4 font-display text-4xl font-bold text-white">
                Five guilds. One shared journey.
              </h2>
              <p className="mt-4 text-slate-400">
                Guilds shape recommendations and routes without erasing your
                progress. Switch whenever your interests change.
              </p>
            </div>
            <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-5">
              {GUILDS.map((guild) => (
                <Card key={guild.id} className="p-5 transition hover:-translate-y-1">
                  <div
                    className="mb-4 h-2 w-12 rounded-full"
                    style={{ backgroundColor: guild.color }}
                  />
                  <h3 className="font-display text-lg font-bold text-white">
                    {guild.shortName}
                  </h3>
                  <p className="mt-2 text-xs leading-5 text-slate-400">
                    {guild.description}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section
          id="sign-in"
          className="mx-auto max-w-5xl px-5 py-24 sm:px-8"
        >
          <Card className="overflow-hidden">
            <div className="grid lg:grid-cols-[.85fr_1.15fr]">
              <div className="bg-gradient-to-br from-indigo-600/35 to-violet-700/10 p-8 sm:p-12">
                <Pill tone="cyan">Course portal access</Pill>
                <h2 className="mt-5 font-display text-4xl font-bold text-white">
                  Ready to enter?
                </h2>
                <p className="mt-4 leading-7 text-slate-300">
                  Continue with Google to create your portal account or sign
                  back in. Invited students can also use a secure email link.
                </p>
                <div className="mt-8 flex items-center gap-3 text-sm text-slate-400">
                  <Icon name="ShieldAlert" size={20} className="text-emerald-300" />
                  No portal password to remember.
                </div>
              </div>
              <div className="p-8 sm:p-12">
                <Button
                  className="w-full"
                  type="button"
                  variant="secondary"
                  disabled={googleLoading}
                  onClick={() => void handleGoogle()}
                >
                  <Icon name="UserPlus" size={18} />
                  {googleLoading ? "Opening Google..." : "Continue with Google"}
                </Button>

                <div className="my-7 flex items-center gap-3">
                  <span className="h-px flex-1 bg-slate-800" />
                  <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    or use email
                  </span>
                  <span className="h-px flex-1 bg-slate-800" />
                </div>

                <form onSubmit={handleEmail}>
                  <label
                    htmlFor="email"
                    className="text-sm font-bold text-slate-200"
                  >
                    Invited email
                  </label>
                  <div className="relative mt-2">
                    <Icon
                      name="Mail"
                      size={18}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500"
                    />
                    <input
                      id="email"
                      type="email"
                      required
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      placeholder="you@example.edu"
                      className="min-h-12 w-full rounded-xl border border-slate-700 bg-slate-950 pl-11 pr-4 text-sm text-white placeholder:text-slate-600"
                    />
                  </div>
                  <Button
                    className="mt-4 w-full"
                    type="submit"
                    disabled={loading}
                  >
                    <Icon name="Rocket" size={18} />
                    {loading ? "Sending link..." : "Send magic link"}
                  </Button>
                  {message && (
                    <p className="mt-4 rounded-xl border border-cyan-400/20 bg-cyan-400/5 p-3 text-xs leading-5 text-cyan-100">
                      {message}
                    </p>
                  )}
                </form>

                {isDemo && (
                  <div className="mt-8 border-t border-slate-800 pt-7">
                    <p className="mb-3 text-center text-[11px] font-bold uppercase tracking-wider text-slate-500">
                      Explore without a backend
                    </p>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <Button
                        variant="secondary"
                        onClick={() => signInDemo("student")}
                      >
                        <Icon name="Gamepad2" size={18} />
                        Student demo
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() => signInDemo("instructor")}
                      >
                        <Icon name="WandSparkles" size={18} />
                        Faculty demo
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
        </section>
      </main>

      <footer className="border-t border-slate-800 px-5 py-8 text-center text-xs text-slate-600">
        The Wonderful World of Essential Math for Games
        <span className="mx-2">·</span>
        <Link to="/" className="hover:text-slate-300">
          Adventure Portal
        </Link>
      </footer>
    </div>
  );
}
